import {
  PricingSearchNonLoanValues,
  EppsLoanInformation,
  isGovtLoan,
  getUpfrontPPEFeeValue,
} from '@frontend/pricing-search';
import { UseFormReturn } from 'react-hook-form';
import api from '../utils/api';
import { prepareLoanDocTypes } from '../app-helpers';
import { getUserRightsWithIds } from './Rights';
import Common from './Common';
import Session, {
  APPLICATION_NAME,
  IS_BUYSIDE,
  IS_LO,
  IS_SELLSIDE,
  IS_VALIDATE,
  JWT_TOKEN,
} from './Session';

interface InitSchemaFormOptions {
  loanInfo: any;
  roleName: string;
  allLookups: any;
  documentationTypes?: any[];
  canAssignLoanOfficer?: boolean;
  viewDeliveryAndCommitment?: boolean;
}

export const initSchemaForm = (form, options: InitSchemaFormOptions) => {
  const {
    loanInfo,
    roleName,
    allLookups,
    documentationTypes = [],
    canAssignLoanOfficer = true,
    viewDeliveryAndCommitment = true,
  } = options;

  if (loanInfo && roleName && Object.keys(loanInfo).length > 0) {
    const userRights = getUserRightsWithIds() || [];
    const clientSettings = allLookups?.clientSettings?.[0] || {};
    const encompassSettings = clientSettings?.encompassSettings || {};
    const clientOptions = clientSettings?.options || {};
    const nonLoanValues: PricingSearchNonLoanValues = {
      role: roleName ?? '',
      canAssignLoanOfficer,
      viewDeliveryAndCommitment,
      isBuyside: Session.get(IS_BUYSIDE),
      isSellside: Session.get(IS_SELLSIDE),
      isValidate: Session.get(IS_VALIDATE),
      lockStatus: '',
      isTPO: Session.get(APPLICATION_NAME) === 'TPO',
      loanChannel: loanInfo?.loanInformation?.loanChannel,
      userRights,
      requestLockType: loanInfo?.requestLockType,
      skipIncome: clientOptions?.skipIncome,
      skipAssets: clientOptions?.skipAssets,
      allowLock: clientOptions?.allowLock,
      roundCorr: clientOptions?.roundCorr,
      roundBroker: clientOptions?.roundBroker,
      noClosingCost: clientOptions?.noClosingCost,
      flagged: clientOptions?.flagged,
      hideLockRequest: clientOptions?.hideLockRequest,
      icePpeFloat: clientOptions?.icePpeFloat,
      isMIQuotesEnabled: clientOptions?.isMIQuotesEnabled,
      isBuySideHistorical: clientOptions?.isBuySideHistorical,
      changeEffectiveDateOnReLock:
        encompassSettings?.changeEffectiveDateOnReLock,
      changeProgramOnReLock: encompassSettings?.changeProgramOnReLock,
      changeAdditionalLoanProg: encompassSettings?.changeAdditionalLoanProg,
    };
    Object.assign(loanInfo, { nonLoanValues });
    prepareLoanDocTypes(loanInfo, documentationTypes);
    form.reset(loanInfo);
    form.setValue('nonLoanValues.role', roleName ?? '');
    const existingTerm = loanInfo?.loanInformation?.term;
    const hasCustomTerm = existingTerm != null && existingTerm !== '';
    if (!loanInfo?.productType?.length && !hasCustomTerm) {
      const defaultTerms = (allLookups?.loanTerms ?? []).map((t) => t.value);
      if (defaultTerms.length > 0) {
        form.setValue('productType', defaultTerms);
      }
    }
  }
};

export const getGeocodes = async (loanId) => {
  let result;
  if (Session.get(IS_LO)) {
    const patToken = await Common.getPATToken();
    const originId = await Common.getOriginId();
    result = await api.getGeocodesV1({ patToken, originId }, loanId);
  } else {
    const jwtToken = await Session.get(JWT_TOKEN);
    result = await api.getGeocodesV2({ jwtToken }, loanId);
  }
  return result;
};

export const getLoanLimits = async (loanData) => {

  try {
    let result;
    if (Session.get(IS_LO)) {
      const patToken = await Common.getPATToken();
      const originId = await Common.getOriginId();
      result = await api.getLoanLimitsV1({ patToken, originId }, loanData);
    } else {
      const jwtToken = await Session.get(JWT_TOKEN);
      result = await api.getLoanLimitsV2({ jwtToken }, loanData);
    }

    return result;
  } catch (e) {
    console.error(e);
  }
  return [];
};

export const getZipLocations = async (zip) => {
  let locations = [];
  if (Session.get(IS_LO)) {
    locations = await api.getWebhookZipLocations(zip);
  } else {
    locations = await api.getZipLocations(zip, {
      customToken: { value: Session.get(JWT_TOKEN) },
      tokenType: 'Bearer',
    });
  }
  return locations;
};

export const getStateCounties = async (stateID) => {
  let counties = [];
  if (Session.get(IS_LO)) {
    counties = await api.getWebhookStateCounties(stateID);
  } else {
    counties = await api.getStateCounties(stateID, {
      customToken: { value: Session.get(JWT_TOKEN) },
      tokenType: 'Bearer',
    });
  }
  return counties;
};

export const getAllLookups = async () => {
  let response: Response | any[] = [];
  if (Session.get(IS_LO)) {
    response = await api.getWebhookLookups('all');
  } else {
    response = await api.getAllLookups({
      customToken: { value: Session.get(JWT_TOKEN) },
      tokenType: 'Bearer',
    });
  }
  // TODO(ngen): this was using response.code, but fetch statements use response.status
  // This error flow never would have triggered, and would have resulted in a silent error with empty lookup options on screen
  if ((response as Response)?.status) {
    throw new Error((response as Response)?.status.toString());
  }
  return response;
};

export const calculateGovie = async (opts: {
  baseLoanAmount: number;
  feeAmountFinanced: number | null;
  feeFactor: number | null;
  productType: string;
  systemFeeFactor: number;
  sourceSystem: string;
  loanGuid: string;
  sourceRefId: string;
}) => {
  return api.calculateGovie(opts as any);
};

interface HandleGovtFeeUpdateOptions {
  govtUpfrontFees: any[];
}

export interface GovtFeeUpdateResult {
  feeAmountFinanced: number;
}

export const handleGovtFeeUpdate = async (
  form: UseFormReturn<any>,
  opts: HandleGovtFeeUpdateOptions,
): Promise<GovtFeeUpdateResult | undefined> => {
  const { govtUpfrontFees } = opts;
  const values = form.getValues();
  const { standardProducts, specialProducts } = values;
  const propertyValue = values.property?.value;
  const {
    ltv: rawLtv,
    loanPurpose,
    vaFirstTimeUse,
    lienPosition,
    firstMortgageAmount,
    secondMortgageAmount,
    feeFactor,
    usdaGuarantee,
    feeAmountFinanced,
    scenario,
    vaFundingFeeExempted,
  } = values.loanInformation || {};

  const mortgageAmount = lienPosition === 2 ? secondMortgageAmount : firstMortgageAmount;
  const ltv = rawLtv ?? (propertyValue ? parseFloat(((mortgageAmount / propertyValue) * 100).toFixed(3)) : undefined);

  if (!isGovtLoan(standardProducts, specialProducts)) {
    return;
  }

  const isUSDA = standardProducts?.includes(
    EppsLoanInformation.StandardProducts.USDA,
  );
  const isVA = standardProducts?.includes(
    EppsLoanInformation.StandardProducts.VA,
  );

  if (isUSDA) {
    const baseLoanAmount =
      lienPosition === 1 ? firstMortgageAmount : secondMortgageAmount;
    const feeFactorValue = feeFactor || usdaGuarantee || 1;
    const financedAmt =
      scenario === 'FULLY_FINANCED' ? null : feeAmountFinanced || 0;
    const usdaResult = await calculateGovie({
      baseLoanAmount,
      feeAmountFinanced: financedAmt,
      feeFactor: feeFactorValue,
      productType: 'usda',
      systemFeeFactor: 1,
      sourceSystem: '',
      loanGuid: '',
      sourceRefId: '',
    });
    if (usdaResult) {
      form.setValue('loanInformation.feeAmount', usdaResult.totalFeeAmount);
      form.setValue('loanInformation.financedAmount', usdaResult.feeAmountFinanced);
      form.setValue('loanInformation.feeAmountFinanced', usdaResult.feeAmountFinanced);
      form.setValue('loanInformation.feeAmountPaidinCash', usdaResult.feeAmountPaidInCash);
      form.setValue('loanInformation.govtFeeFactor', usdaResult.feePercent);
      form.setValue('loanInformation.feeFactor', Number(feeFactorValue));
      form.setValue('loanInformation.feePercent', usdaResult.feePercent);
      form.setValue('loanInformation.normalizedFeeFactor', usdaResult.normalizedFeeFactor);
      form.setValue('loanInformation.usdaGuarantee', Number(feeFactorValue));
    }
    return { feeAmountFinanced: usdaResult?.feeAmountFinanced ?? 0 };
  }

  let govtFeeFactor: number | null = null;
  if (isVA && vaFundingFeeExempted) {
    govtFeeFactor = 0;
  } else {
    govtFeeFactor = getUpfrontPPEFeeValue(
      { standardProducts, specialProducts },
      { govtUpfrontFees, loanPurpose, vaFirstTimeUse: vaFirstTimeUse ?? true, ltv },
    );
  }

  if (govtFeeFactor == null) return;

  form.setValue('loanInformation.govtFeeFactor', govtFeeFactor);

  const baseLoanAmount =
    lienPosition === 1 ? firstMortgageAmount : secondMortgageAmount;
  if (typeof baseLoanAmount === 'number' && baseLoanAmount > 0) {
    const feeAmount = parseFloat(
      (baseLoanAmount * (govtFeeFactor / 100)).toFixed(2),
    );
    form.setValue('loanInformation.feeAmount', feeAmount);
    form.setValue('loanInformation.financedAmount', Math.floor(feeAmount));
    form.setValue('loanInformation.feeAmountFinanced', Math.floor(feeAmount));
    form.setValue(
      'loanInformation.feeAmountPaidinCash',
      parseFloat((feeAmount % 1).toFixed(2)),
    );
    return { feeAmountFinanced: Math.floor(feeAmount) };
  }
};
